import React, { useEffect, useRef, ReactNode } from 'react';
import cn from 'classnames';
import NavItemComponent from './NavItem';

export type NavItem = {
	href: string;
	label: string;
	target?: string;
	isActive?: boolean;
	isDisabled?: boolean;
	classNames?: string[];
	children?: NavItem[] | ReactNode;
};

export interface INavProps {
	className?: string;
	classNames?: string[];
	navItems?: NavItem[];
	justifyContent?: 'start' | 'center' | 'end';
	vertical?: boolean;
	type?: 'links' | 'tabs' | 'pills';
	dropdownEnabled?: boolean;
	dropdownAutoClose?: 'true' | 'false' | 'inside' | 'outside';
	collapseEnabled?: boolean;
	collapseOptions?: { [x: string]: any };
	children?: React.ReactNode;
}

/**
 * Nav
 * @block
 * @requiresHooks
 */
const Nav: React.FC<INavProps> = ({
	navItems,
	children,
	className,
	classNames = [],
	justifyContent = 'start',
	vertical = false,
	type = 'links',
	dropdownEnabled = true,
	dropdownAutoClose,
	collapseEnabled = false,
}) => {
	const ref = useRef(null);

	// @ts-ignore
	useEffect(() => {
		collapseEnabled && import('bootstrap/js/dist/collapse').then(({ default: Collapse }) => {
			if (!ref.current) {
				return;
			}

			const collapseElementList: HTMLDivElement[] = [].slice.call(
				(ref.current! as HTMLDivElement).querySelectorAll(
					'.collapse-toggle',
				),
			);
			collapseElementList.map((collapseToggleEl) => {
				const collapseEl =
					collapseToggleEl.nextElementSibling as HTMLDivElement;
				const collapse = new Collapse(collapseEl, {
					toggle: false,
				});

				collapseToggleEl.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation();
					collapse.toggle();
				});

				collapseEl.addEventListener('show.bs.collapse', () => {
					collapseToggleEl.parentElement?.classList.add(
						'collapse-open',
					);
				});

				collapseEl.addEventListener('hide.bs.collapse', () => {
					collapseToggleEl.parentElement?.classList.remove(
						'collapse-open',
					);
				});
			});
		});

		dropdownEnabled && import('bootstrap/js/dist/dropdown').then(({ default: Dropdown }) => {
			if (!ref.current) {
				return;
			}

			const dropdownElementList: HTMLDivElement[] = [].slice.call(
				(ref.current! as HTMLDivElement).querySelectorAll(
					'.dropdown-toggle',
				),
			);
			dropdownElementList.map((dropdownToggleEl) => {
				dropdownToggleEl.addEventListener(
					'show.bs.dropdown',
					() => {
						dropdownToggleEl
							.closest('.nav')
							?.classList.add('dropdown-visible');

						dropdownToggleEl.parentElement?.classList.add(
							'dropdown-open',
						);
					},
				);

				dropdownToggleEl.addEventListener(
					'hide.bs.dropdown',
					() => {
						dropdownToggleEl
							.closest('.nav')
							?.classList.remove('dropdown-visible');

						dropdownToggleEl.parentElement?.classList.remove(
							'dropdown-open',
						);
					},
				);
			});
		});
	}, []);

	// On Heemans, when trying to render the Footer in the SSR server, I'm seeing an error because it's
	// decoding an array prop as an object { "0": "first", "1": "second" } as opposed to [ 'first', 'second' ].
	// This might be pointing to the same thing that is causing the odd characters issue on Legate.  This is a note
	// to fix the problem ( so _navItems.map is a function always ) but also to remind myself of the thread to pull on.
	// @ts-ignore
	const _navItems = navItems
		? ((Array.isArray(navItems)
			? navItems
			: Object.values(navItems)) as NavItem[])
		: null;

	return (
		<ul
			ref={ref}
			className={
				className ||
				cn(
					'nav',
					`justify-content-${justifyContent}`,
					vertical === true && 'flex-column',
					type === 'tabs' && 'nav-tabs',
					type === 'pills' && 'nav-pills',
					...classNames,
				)
			}
		>
			{_navItems &&
			_navItems.map((item, index) => <NavItemComponent
				item={item}
				key={index}
				dropdownEnabled={dropdownEnabled}
				dropdownAutoClose={dropdownAutoClose}
				collapseEnabled={collapseEnabled}
			/>)}
			{children}
		</ul>
	);
};

export default Nav;
